import { useRef } from "react";
import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface BarChartProps {
  data: Array<{ date: string; count: number }>;
  color?: string;
  height?: number;
}

export function BarChart({ data, color = "#34d399", height = 220 }: BarChartProps) {
  const lastActiveLabelRef = useRef<string | null>(null);

  function handleActiveBar(state: { activeLabel?: string | number } | null) {
    const label = state?.activeLabel == null ? null : String(state.activeLabel);
    if (!label || label === lastActiveLabelRef.current) return;
    lastActiveLabelRef.current = label;
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(8);
    }
  }

  function clearActiveBar() {
    lastActiveLabelRef.current = null;
  }

  return (
    <div style={{ width: "100%", height }} onTouchEnd={clearActiveBar}>
      <ResponsiveContainer>
        <RBarChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 0, left: -8 }}
          onMouseMove={handleActiveBar}
          onMouseLeave={clearActiveBar}
        >
          <CartesianGrid stroke="rgba(148,163,184,0.08)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: "hsl(215 20% 55%)", fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: string) => v.slice(5)}
            minTickGap={24}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: "hsl(215 20% 55%)", fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={32}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(148,163,184,0.06)" }}
            contentStyle={{
              background: "hsl(222 47% 8%)",
              border: "1px solid hsl(217 32% 18%)",
              borderRadius: 6,
              fontSize: 12,
            }}
            labelStyle={{ color: "hsl(215 20% 65%)" }}
            itemStyle={{ color: "hsl(210 40% 98%)" }}
            labelFormatter={formatDateLabel}
            formatter={formatTooltipValue}
          />
          <Bar yAxisId="left" dataKey="count" fill={color} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatDateLabel(value: unknown) {
  const raw = String(value ?? "");
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return raw;

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
  }).format(new Date(year, month - 1, day));
}

function formatTooltipValue(value: unknown) {
  const numericValue = Number(value);
  const label = "Count";
  const formattedValue = Number.isFinite(numericValue)
    ? numericValue.toLocaleString()
    : String(value ?? "");

  return [formattedValue, label];
}
